import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type OnboardingStep = 'welcome' | 'role' | 'wallet' | 'pin' | 'eula' | 'confirmation'
export type TutorialStep = 'profile' | 'navigation' | 'market' | 'portfolio'

const ONBOARDING_STEPS: OnboardingStep[] = ['welcome', 'role', 'wallet', 'pin', 'eula', 'confirmation']
const TUTORIAL_STEPS: TutorialStep[] = ['profile', 'navigation', 'market', 'portfolio']

const STORAGE_KEY = 'nomadian_onboarding'

interface PersistedOnboardingState {
    currentStep: OnboardingStep
    completedSteps: OnboardingStep[]
    tutorialCompleted: boolean
    currentTutorialStep: TutorialStep | null
}

function loadPersistedState(): PersistedOnboardingState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        return JSON.parse(raw) as PersistedOnboardingState
    } catch {
        return null
    }
}

function persistState(state: PersistedOnboardingState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export const useOnboardingStore = defineStore('onboarding', () => {
    const persisted = loadPersistedState()

    const currentStep = ref<OnboardingStep>(persisted?.currentStep ?? 'welcome')
    const completedSteps = ref<OnboardingStep[]>(persisted?.completedSteps ?? [])
    const tutorialCompleted = ref(persisted?.tutorialCompleted ?? false)
    const currentTutorialStep = ref<TutorialStep | null>(
        persisted?.currentTutorialStep ?? null
    )
    const walletCreationInProgress = ref(false)

    const isOnboardingComplete = computed(() =>
        ONBOARDING_STEPS.every((s) => completedSteps.value.includes(s))
    )
    const stepIndex = computed(() => ONBOARDING_STEPS.indexOf(currentStep.value) + 1)
    const totalSteps = ONBOARDING_STEPS.length
    const isTutorialActive = computed(
        () => !tutorialCompleted.value && currentTutorialStep.value !== null
    )
    const tutorialStepIndex = computed(() => {
        if (!currentTutorialStep.value) return 0
        return TUTORIAL_STEPS.indexOf(currentTutorialStep.value) + 1
    })
    const totalTutorialSteps = TUTORIAL_STEPS.length

    watch(
        [currentStep, completedSteps, tutorialCompleted, currentTutorialStep],
        () => {
            persistState({
                currentStep: currentStep.value,
                completedSteps: completedSteps.value,
                tutorialCompleted: tutorialCompleted.value,
                currentTutorialStep: currentTutorialStep.value,
            })
        },
        { deep: true }
    )

    function completeStep(step: OnboardingStep) {
        if (!completedSteps.value.includes(step)) {
            completedSteps.value.push(step)
        }
    }

    function goToStep(step: OnboardingStep) {
        currentStep.value = step
    }

    function nextStep() {
        const idx = ONBOARDING_STEPS.indexOf(currentStep.value)
        if (idx < ONBOARDING_STEPS.length - 1) {
            completeStep(currentStep.value)
            currentStep.value = ONBOARDING_STEPS[idx + 1]
        } else {
            completeStep(currentStep.value)
        }
    }

    function previousStep() {
        const idx = ONBOARDING_STEPS.indexOf(currentStep.value)
        if (idx > 0) {
            currentStep.value = ONBOARDING_STEPS[idx - 1]
        }
    }

    function setWalletCreationInProgress(inProgress: boolean) {
        walletCreationInProgress.value = inProgress
    }

    function startTutorial() {
        currentTutorialStep.value = TUTORIAL_STEPS[0]
    }

    function nextTutorialStep() {
        if (!currentTutorialStep.value) return
        const idx = TUTORIAL_STEPS.indexOf(currentTutorialStep.value)
        if (idx < TUTORIAL_STEPS.length - 1) {
            currentTutorialStep.value = TUTORIAL_STEPS[idx + 1]
        } else {
            completeTutorial()
        }
    }

    function previousTutorialStep() {
        if (!currentTutorialStep.value) return
        const idx = TUTORIAL_STEPS.indexOf(currentTutorialStep.value)
        if (idx > 0) {
            currentTutorialStep.value = TUTORIAL_STEPS[idx - 1]
        }
    }

    function completeTutorial() {
        tutorialCompleted.value = true
        currentTutorialStep.value = null
    }

    function resetTutorial() {
        tutorialCompleted.value = false
        currentTutorialStep.value = null
    }

    function reset() {
        currentStep.value = 'welcome'
        completedSteps.value = []
        tutorialCompleted.value = false
        currentTutorialStep.value = null
        walletCreationInProgress.value = false
        localStorage.removeItem(STORAGE_KEY)
    }

    return {
        currentStep,
        completedSteps,
        tutorialCompleted,
        currentTutorialStep,
        walletCreationInProgress,
        isOnboardingComplete,
        stepIndex,
        totalSteps,
        isTutorialActive,
        tutorialStepIndex,
        totalTutorialSteps,
        completeStep,
        goToStep,
        nextStep,
        previousStep,
        setWalletCreationInProgress,
        startTutorial,
        nextTutorialStep,
        previousTutorialStep,
        completeTutorial,
        resetTutorial,
        reset,
    }
})
